'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Droplets, Layers, Sprout, RefreshCw, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ancTierLogService, AncTierLog, AncTier, AncSet, ancSetService } from '@/lib/services/ancService';
import Icon from '@/components/ui/AppIcon';


const TIER_LABELS: Record<AncTier, { label: string; icon: React.ElementType; colour: string; dot: string }> = {
  bottom: { label: 'Bottom', icon: Droplets, colour: 'text-blue-600', dot: 'bg-blue-500' },
  middle: { label: 'Middle', icon: Layers, colour: 'text-amber-600', dot: 'bg-amber-500' },
  top: { label: 'Top', icon: Sprout, colour: 'text-green-600', dot: 'bg-green-500' },
};

function TierBadge({ tier }: { tier: AncTier }) {
  const cfg = TIER_LABELS[tier];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cfg.colour}`}>
      <Icon size={12} />
      {cfg.label}
    </span>
  );
}

function LogCard({ log, onDelete, isAdmin }: { log: AncTierLog; onDelete: (id: string) => void; isAdmin: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const { user } = useAuth();
  const canDelete = isAdmin || log.userId === user?.id;

  const details: { label: string; value: string }[] = [];
  if (log.tier === 'bottom') {
    if (log.leachateVolumeL != null) details.push({ label: 'Volume', value: `${log.leachateVolumeL} L` });
    if (log.leachateColour) details.push({ label: 'Colour', value: log.leachateColour });
  }
  if (log.tier === 'middle') {
    if (log.castingWeightKg != null) details.push({ label: 'Castings', value: `${log.castingWeightKg} kg` });
    if (log.beddingCondition) details.push({ label: 'Bedding', value: log.beddingCondition });
    if (log.moistureLevel) details.push({ label: 'Moisture', value: log.moistureLevel });
    if (log.phReading != null) details.push({ label: 'pH', value: String(log.phReading) });
    if (log.wormMigrationObserved) details.push({ label: 'Migration', value: '✓ Observed' });
  }
  if (log.tier === 'top') {
    if (log.feedType) details.push({ label: 'Feed', value: log.feedType });
    if (log.feedQuantityKg != null) details.push({ label: 'Feed qty', value: `${log.feedQuantityKg} kg` });
    if (log.newBeddingMaterial) details.push({ label: 'Bedding', value: log.newBeddingMaterial });
    if (log.newBeddingQuantityKg != null) details.push({ label: 'Bedding qty', value: `${log.newBeddingQuantityKg} kg` });
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div
        className="flex items-center gap-3 px-3 py-3 cursor-pointer active:bg-muted/50 transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className={`w-1.5 h-10 rounded-full flex-shrink-0 ${TIER_LABELS[log.tier].dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-foreground">{log.setName || 'Unknown Set'}</span>
            <TierBadge tier={log.tier} />
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-muted-foreground">{log.logDate}</span>
            {log.loggedBy && <span className="text-xs text-muted-foreground">· {log.loggedBy}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {canDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(log.id); }}
              className="p-1.5 rounded-lg text-muted-foreground active:text-danger transition-colors"
            >
              <Trash2 size={14} />
            </button>
          )}
          <ChevronDown size={14} className={`text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {expanded && details.length > 0 && (
        <div className="px-3 pb-3 border-t border-border pt-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {details.map((d) => (
              <div key={d.label}>
                <span className="text-xs text-muted-foreground">{d.label}: </span>
                <span className="text-xs font-semibold text-foreground">{d.value}</span>
              </div>
            ))}
          </div>
          {log.notes && (
            <p className="mt-2 text-xs text-muted-foreground italic border-t border-border pt-2">{log.notes}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function AncLogsTable({ refreshTrigger }: { refreshTrigger?: number }) {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState<AncTierLog[]>([]);
  const [sets, setSets] = useState<AncSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterSet, setFilterSet] = useState('');
  const [filterTier, setFilterTier] = useState<AncTier | ''>('');
  const [isLive, setIsLive] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const data = await ancTierLogService.getAll(filterSet || undefined);
    setLogs(data);
    setLoading(false);
  }, [filterSet]);

  useEffect(() => {
    ancSetService.getAll().then(setSets);
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, refreshTrigger]);

  useEffect(() => {
    const channel = ancTierLogService.subscribeToChanges(() => {
      setIsLive(true);
      fetchLogs();
      setTimeout(() => setIsLive(false), 2000);
    });
    return () => { channel.unsubscribe(); };
  }, [fetchLogs]);

  const handleDelete = async (id: string) => {
    const ok = await ancTierLogService.delete(id);
    if (ok) {
      toast.success('Log deleted');
      setLogs((prev) => prev.filter((l) => l.id !== id));
    } else {
      toast.error('Failed to delete');
    }
  };

  const filtered = filterTier ? logs.filter((l) => l.tier === filterTier) : logs;

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <div className="flex-1">
          <h2 className="text-base font-bold text-foreground">Tier Activity Log</h2>
          <p className="text-xs text-muted-foreground">{filtered.length} records</p>
        </div>
        <div className="flex items-center gap-1.5">
          {isLive && (
            <span className="flex items-center gap-1 text-xs text-green-600 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              Live
            </span>
          )}
          <button onClick={fetchLogs} className="p-1.5 rounded-lg text-muted-foreground active:bg-muted transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 py-2 border-b border-border flex gap-2">
        <select
          value={filterSet}
          onChange={(e) => setFilterSet(e.target.value)}
          className="flex-1 rounded-lg border border-border px-2 py-1.5 text-xs bg-input text-foreground focus:outline-none"
        >
          <option value="">All Sets</option>
          {sets.map((s) => <option key={s.id} value={s.id}>{s.setName}</option>)}
        </select>
        <select
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value as AncTier | '')}
          className="flex-1 rounded-lg border border-border px-2 py-1.5 text-xs bg-input text-foreground focus:outline-none"
        >
          <option value="">All Tiers</option>
          <option value="bottom">Bottom</option>
          <option value="middle">Middle</option>
          <option value="top">Top</option>
        </select>
      </div>

      <div className="p-3 space-y-2">
        {loading ? (
          <div className="py-8 flex items-center justify-center">
            <RefreshCw size={20} className="animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No tier logs yet. Start by logging an activity above.</div>
        ) : (
          filtered.map((log) => (
            <LogCard key={log.id} log={log} onDelete={handleDelete} isAdmin={isAdmin} />
          ))
        )}
      </div>
    </div>
  );
}
